import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";
import { notifyError, notifySuccess } from "./notification.slice";
import { authFailCheck } from "./auth.slice";
import PartnerService from "services/PartnerService";

const partnersAdapter = createEntityAdapter({
  selectId: (partner) => partner.id,
});

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (Array.isArray(data) && data[0]?.errorMessage) return data[0].errorMessage;
  if (data.errors?.[0]) return data.errors[0];
  return fallback;
};

const partnersSlice = createSlice({
  name: "partners",
  initialState: partnersAdapter.getInitialState({
    isLoading: false,
    incomeSplit: null,
    incomeSplitType: "this-month",
    ledger: null,
    ledgerLoading: false,
  }),
  reducers: {
    fetchRequest(state) {
      state.isLoading = true;
    },
    fetchFail(state) {
      state.isLoading = false;
    },
    fetchPartnersSuccess(state, action) {
      state.isLoading = false;
      partnersAdapter.setAll(state, action.payload);
    },
    createPartnerSuccess(state, action) {
      state.isLoading = false;
      partnersAdapter.addOne(state, action.payload);
    },
    updatePartnerSuccess(state, action) {
      state.isLoading = false;
      partnersAdapter.upsertOne(state, action.payload);
    },
    deletePartnerSuccess(state, action) {
      state.isLoading = false;
      partnersAdapter.removeOne(state, action.payload);
    },
    fetchIncomeSplitSuccess(state, action) {
      state.isLoading = false;
      state.incomeSplit = action.payload.data;
      state.incomeSplitType = action.payload.type;
    },
    fetchLedgerRequest(state) {
      state.ledgerLoading = true;
    },
    fetchLedgerSuccess(state, action) {
      state.ledgerLoading = false;
      state.ledger = action.payload;
    },
    fetchLedgerFail(state) {
      state.ledgerLoading = false;
    },
    clearLedger(state) {
      state.ledger = null;
      state.ledgerLoading = false;
    },
  },
});

export const partnerSelectors = partnersAdapter.getSelectors(
  (state) => state.partners
);

export default partnersSlice.reducer;

export const fetchPartners = () => async (dispatch) => {
  dispatch(partnersSlice.actions.fetchRequest());
  const response = await PartnerService.get();
  if (response.ok) {
    dispatch(partnersSlice.actions.fetchPartnersSuccess(response.data || []));
  } else {
    dispatch(authFailCheck(response));
    dispatch(partnersSlice.actions.fetchFail());
  }
};

export const fetchPartnerLedger = (partnerId, type) => async (
  dispatch,
  getState
) => {
  if (!partnerId) return;
  const periodType = type || getState().partners.incomeSplitType || "this-month";
  dispatch(partnersSlice.actions.fetchLedgerRequest());
  const response = await PartnerService.getLedger(partnerId, periodType);
  if (response.ok) {
    dispatch(partnersSlice.actions.fetchLedgerSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(partnersSlice.actions.fetchLedgerFail());
    dispatch(
      notifyError(
        getErrorMessage(response.data, "Sherik hisobini yuklashda xatolik yuz berdi.")
      )
    );
  }
};

export const clearPartnerLedger = () => (dispatch) => {
  dispatch(partnersSlice.actions.clearLedger());
};

export const fetchPartnerIncomeSplit = (type) => async (dispatch, getState) => {
  const splitType = type || getState().partners.incomeSplitType || "this-month";
  dispatch(partnersSlice.actions.fetchRequest());
  const response = await PartnerService.getIncomeSplit(splitType);
  if (response.ok) {
    dispatch(
      partnersSlice.actions.fetchIncomeSplitSuccess({
        data: response.data,
        type: splitType,
      })
    );
  } else {
    dispatch(authFailCheck(response));
    dispatch(partnersSlice.actions.fetchFail());
  }
};

export const createPartner = (data) => async (dispatch) => {
  dispatch(partnersSlice.actions.fetchRequest());
  const response = await PartnerService.create(data);
  if (response.ok) {
    dispatch(notifySuccess(`${data.name} sherik yaratildi.`));
    dispatch(fetchPartners());
    dispatch(fetchPartnerIncomeSplit());
    return { ok: true };
  }
  dispatch(authFailCheck(response));
  dispatch(partnersSlice.actions.fetchFail());
  return {
    ok: false,
    message: getErrorMessage(response.data, "Sherik yaratishda xatolik yuz berdi."),
  };
};

export const updatePartner = (id, data) => async (dispatch) => {
  dispatch(partnersSlice.actions.fetchRequest());
  const response = await PartnerService.update(id, data);
  if (response.ok) {
    dispatch(notifySuccess(`${data.name} o'zgartirildi.`));
    dispatch(fetchPartners());
    dispatch(fetchPartnerIncomeSplit());
    return { ok: true };
  }
  dispatch(authFailCheck(response));
  dispatch(partnersSlice.actions.fetchFail());
  return {
    ok: false,
    message: getErrorMessage(response.data, "Sherikni o'zgartirishda xatolik yuz berdi."),
  };
};

export const deletePartner = (id) => async (dispatch, getState) => {
  dispatch(partnersSlice.actions.fetchRequest());
  const current = partnerSelectors.selectById(getState(), id);
  const response = await PartnerService.delete(id);
  if (response.ok) {
    dispatch(partnersSlice.actions.deletePartnerSuccess(id));
    dispatch(notifySuccess(`${current?.name || "Sherik"} o'chirildi.`));
    dispatch(fetchPartnerIncomeSplit());
  } else {
    dispatch(authFailCheck(response));
    dispatch(partnersSlice.actions.fetchFail());
    dispatch(
      notifyError(
        getErrorMessage(response.data, "Sherikni o'chirishda xatolik yuz berdi.")
      )
    );
  }
};
